import React, { useState, useEffect } from 'react';
import { Save, Settings, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarksheetSetting = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    institute_name: '',
    institute_address: '',
    logo_url: '',
    show_grade: true,
    show_grade_point: true,
    show_percentage: true,
    show_rank: false,
    show_remarks: true,
    principal_name: '',
    principal_signature_url: '',
    watermark_text: '',
    footer_text: 'This is a computer-generated document'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // First try to get existing marksheet settings
      try {
        const response = await axios.get(`${API_URL}/api/examination/marksheet-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (err) {
        // If no settings exist, fetch college info as defaults
        const collegeResponse = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (collegeResponse.data) {
          setSettings(prev => ({
            ...prev,
            institute_name: collegeResponse.data.name || '',
            institute_address: collegeResponse.data.address || '',
            logo_url: collegeResponse.data.logo || '',
            principal_name: collegeResponse.data.principal_name || '',
            watermark_text: collegeResponse.data.name || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_URL}/api/examination/marksheet-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Marksheet settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mark Sheet Setting</h1>
          <p className="text-sm text-gray-500 mt-1">Configure mark sheet template and settings</p>
        </div>
        <button 
          onClick={handleSave} 
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Settings
        </button>
      </div>

      {message.text && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <Settings className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              These settings will be applied to all generated mark sheets. Configure what information should appear on the mark sheet.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold mb-4">Institute Information</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institute Name *</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.institute_name} 
              onChange={(e) => setSettings({ ...settings, institute_name: e.target.value })} 
              placeholder="Enter institute name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institute Address</label>
            <textarea 
              rows="3" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.institute_address} 
              onChange={(e) => setSettings({ ...settings, institute_address: e.target.value })}
              placeholder="Enter full address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <input 
              type="url" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.logo_url} 
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} 
              placeholder="https://example.com/logo.png" 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Principal Name</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.principal_name} 
              onChange={(e) => setSettings({ ...settings, principal_name: e.target.value })}
              placeholder="Dr. John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Principal Signature URL</label>
            <input 
              type="url" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.principal_signature_url} 
              onChange={(e) => setSettings({ ...settings, principal_signature_url: e.target.value })} 
              placeholder="https://example.com/signature.png" 
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold mb-4">Display Settings</h3>
          
          <div className="space-y-2">
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="show_grade" 
                checked={settings.show_grade} 
                onChange={(e) => setSettings({ ...settings, show_grade: e.target.checked })} 
                className="rounded" 
              />
              <label htmlFor="show_grade" className="ml-3 text-sm text-gray-700">Show Grade</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="show_grade_point" 
                checked={settings.show_grade_point} 
                onChange={(e) => setSettings({ ...settings, show_grade_point: e.target.checked })} 
                className="rounded" 
              />
              <label htmlFor="show_grade_point" className="ml-3 text-sm text-gray-700">Show Grade Point</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="show_percentage" 
                checked={settings.show_percentage} 
                onChange={(e) => setSettings({ ...settings, show_percentage: e.target.checked })} 
                className="rounded" 
              />
              <label htmlFor="show_percentage" className="ml-3 text-sm text-gray-700">Show Percentage</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="show_rank" 
                checked={settings.show_rank} 
                onChange={(e) => setSettings({ ...settings, show_rank: e.target.checked })} 
                className="rounded" 
              />
              <label htmlFor="show_rank" className="ml-3 text-sm text-gray-700">Show Rank</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="show_remarks" 
                checked={settings.show_remarks} 
                onChange={(e) => setSettings({ ...settings, show_remarks: e.target.checked })} 
                className="rounded" 
              />
              <label htmlFor="show_remarks" className="ml-3 text-sm text-gray-700">Show Remarks</label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Watermark Text</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.watermark_text} 
              onChange={(e) => setSettings({ ...settings, watermark_text: e.target.value })}
              placeholder="Institute name or logo text"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded-lg" 
              value={settings.footer_text} 
              onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })} 
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mt-6">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <h4 className="font-medium text-gray-900">Preview</h4>
            </div>
            <div className="bg-white p-4 rounded border text-sm">
              <div className="text-center border-b pb-2 mb-2">
                <p className="font-bold">{settings.institute_name || 'Institute Name'}</p>
                {settings.institute_address && <p className="text-xs text-gray-600">{settings.institute_address}</p>}
              </div>
              <p className="text-xs text-gray-600 mb-2">MARK SHEET</p>
              <div className="text-xs text-gray-600">
                <p>Shows: {[settings.show_grade && 'Grade', settings.show_grade_point && 'GPA', settings.show_percentage && 'Percentage', settings.show_rank && 'Rank'].filter(Boolean).join(', ') || 'None'}</p>
              </div>
              <p className="text-xs text-gray-600 mt-4 text-center italic">{settings.footer_text}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarksheetSetting;
